import { useState } from 'react'
import RowMenu from '../row_menu'
import { Button, Badge, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import { PlusCircle } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import useStore from '../../hooks/use_store'
import { statusTypes } from '../../constants/status_colour_map'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const AssetsCollectionTable = ({ cycle, assetId, assetType, childAssets, parentAsset }) => {
  const cycleID = cycle.id
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const { createModel, deleteModel } = useStore()
  const goToAsset = (data) => navigate(`${data.id}`)

  const createAsset = () => {
    createModel.mutate(
      {
        endpoint: `cycles/${cycleID}/assets`,
        data: {
          cycle_id: cycleID,
          title: `New ${assetType?.label}`,
          asset_type: assetType.value,
          parent_asset_id: assetId,
          // Group parents may not have dates yet (they're derived from children), so fall back to the cycle's
          start_date: parentAsset?.start_date ?? cycle.start_date,
          end_date: parentAsset?.end_date ?? cycle.end_date,
        },
      },
      {
        onSuccess: (res) => {
          if (res.data.asset) {
            goToAsset(res.data.asset)
          }
        },
      }
    )
  }
  const showValidatorsResult = (data) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {data.errors > 0 && <Badge tone='error'>{`Errors: ${data.errors}`}</Badge>}
        {data.warnings > 0 && <Badge tone='warning'>{`Warnings: ${data.warnings}`}</Badge>}
        {!data.warnings && !data.errors && <Badge tone='success'>OK</Badge>}
      </div>
    )
  }
  const columns = [
    { key: 'asset_title', header: 'Title', render: (data) => data.default_title },
    { key: 'validators', header: 'Validation', render: (data) => showValidatorsResult(data) },
    {
      key: 'status',
      header: 'Status',
      render: (data) => (
        <Badge tone={statusTypes[data?.status?.toLowerCase()] ?? 'neutral'}>{data.status ?? 'N/A'}</Badge>
      ),
    },
    ...(assetType.value === 'episode'
      ? [
          {
            key: 'pre_roll',
            header: 'Pre-Roll',
            render: (data) => (
              <div className='py-2 flex flex-wrap gap-2'>
                {data.categories
                  ?.filter((category) => category.type === 'warning-slates' || category.type === 'ad-pools')
                  .map((category) => (
                    <Badge key={category.id}>
                      {category.type === 'warning-slates'
                        ? category.category_values?.[0]?.value
                        : category.external_unique_name || MISSING_UNIQUE_NAME}
                    </Badge>
                  ))}
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <div className='flex justify-end'>
          <RowMenu
            label={`Actions for ${data.title}`}
            items={[
              {
                id: 'delete',
                label: 'Delete',
                tone: 'danger',
                onSelect: () =>
                  deleteModel.mutate({
                    endpoint: `cycles/${cycleID}/assets`,
                    id: data.id,
                    deleteNoun: assetType?.label?.toLowerCase() || 'asset',
                    deleteName: data.title,
                    onSuccess: () => {
                      queryClient.invalidateQueries(`cycles/${cycleID}/assets/${data.parent_asset_id}`)
                    },
                  }),
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <DataTable
        caption={assetType?.plural_label}
        columns={columns}
        rows={childAssets ?? []}
        onRowClick={goToAsset}
        isLoading={deleteModel.isLoading}
        empty={
          <EmptyState
            title={`No ${assetType?.plural_label?.toLowerCase()} found!`}
            detail={`Add ${assetType?.label?.toLowerCase()} to the asset to begin.`}
          />
        }
        searching={{ search, onSearch: setSearch }}
        actions={
          <Button leadingIcon={<PlusCircle size={20} />} onClick={() => createAsset()}>
            Create {assetType?.label}
          </Button>
        }
      />
    </>
  )
}

export default AssetsCollectionTable
